import { ICapabilities } from 'library/ts/interfaces/utilities/capabilities/ICapabilities';
import { WindowEvent } from 'library/ts/ui/events/WindowEvent';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description ConnectionMonitor internet connection monitor
 *
 * Tracks browser connectivity via two complementary mechanisms that both update the same
 * `#onLine` field and dedupe on change, so `callback` never fires twice for one transition
 * regardless of which mechanism detects it first:
 * 1. Native `online`/`offline` window events — the primary detection path, registered for every
 *    browser.
 * 2. A Firefox-only 200ms poll of `navigator.onLine` — a defensive fallback historically needed
 *    because Firefox didn't reliably fire those events (see the constructor's Firefox-specific
 *    block for the full history and references).
 */
export class ConnectionMonitor {

	#utils: Utilities;
	#capabilities: ICapabilities;
	#callback: ( online: boolean ) => void;
	#onLine: boolean = navigator.onLine;
	#logger: ILogger;

	// Firefox connectivity polling interval id (see constructor); stored so destroy() can clear it
	#pollIntervalId: ReturnType<typeof setInterval> | undefined;

	// List of online events
	#events: WindowEvent[] = [
		WindowEvent.ONLINE,
		WindowEvent.OFFLINE
	];

	/**
	 * @description Constructor
	 * @param {Utilities} utils
	 * @param {ICapabilities} capabilities
	 * @param {(online: boolean) => void} callback
	 */
	constructor( utils: Utilities, capabilities: ICapabilities, callback: ( online: boolean ) => void ) {
		this.#utils = utils;
		this.#capabilities = capabilities;
		this.#callback = callback;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );

		// add events listeners
		for ( let i: number = 0; i < this.#events.length; i++ ) {
			window.addEventListener( this.#events[ i ], this.#onConnectionChange );
		}

		// Historical Firefox-only reliability fallback. For years, `navigator.onLine` and the
		// native online/offline events were effectively broken on desktop Firefox —
		// `navigator.onLine` stayed `true` regardless of actual connectivity unless the user
		// manually chose "Work Offline" in the menu, and the online/offline DOM events simply
		// didn't fire when the underlying network interface changed:
		// https://bugzilla.mozilla.org/show_bug.cgi?id=654579
		// Mozilla fixed this for OS X and Windows in Firefox 41 (2015):
		// https://developer.mozilla.org/en-US/docs/Mozilla/Firefox/Releases/41
		// with the remaining Linux-specific gap closed separately shortly after:
		// https://bugzilla.mozilla.org/show_bug.cgi?id=1173931
		// No regressions have been reported since, so on any currently supported Firefox this
		// poll should be a no-op in practice — kept as a low-cost safety net, not because it's
		// known to still be needed. `isFirefox` already excludes Firefox for iOS (renders via
		// WebKit, not Gecko, so it never had this issue) — see Capabilities#isFirefoxIOS.
		if ( this.#capabilities?.isFirefox === true ) {
			this.#logger.log( 'Firefox', 'start polling onLine status' );

			this.#pollIntervalId = setInterval(
				() => {
					if ( this.#onLine !== navigator.onLine ) {
						this.#onLine = navigator.onLine;
						if ( this.#utils?.Type?.isFunction( this.#callback ) ) {
							this.#callback( this.#onLine );
						}
					}
				},
				200
			);
		}
	}

	get name(): string {
		return 'ConnectionMonitor';
	}

	/**
	 * @description Up-to-date online/offline status, kept in sync via native window events (all
	 * browsers) and, for Firefox, an additional polling fallback (see constructor). Reflects the
	 * browser's own connectivity heuristic (network link state), not actual internet reachability
	 * — a device connected to a LAN with no internet access still reports online. Treat this as a
	 * hint, not a guarantee.
	 * @return {boolean}
	 */
	get onLine(): boolean {
		return this.#onLine;
	}

	/**
	 * @description Handles online/offline events on window and dispatches application-wide events.
	 * Declared as a class-field arrow function (not a prototype method or `.bind()` call site) so
	 * the exact same function reference is used both here and in `destroy()`'s
	 * `removeEventListener` calls — using a different reference for either would silently fail to
	 * unsubscribe and leak the listener.
	 * @param {Event} event
	 * @private
	 */
	#onConnectionChange = ( event: Event ): void => {
		const eventTypeIsOnline: boolean = event?.type === WindowEvent.ONLINE;

		// set online property if it is different from last one
		if ( this.#onLine !== eventTypeIsOnline ) {
			this.#onLine = eventTypeIsOnline;
			if ( this.#utils?.Type?.isFunction( this.#callback ) ) {
				this.#callback( this.#onLine );
			}
		}
	};

	/**
	 * @description Removes the online/offline window listeners and clears the Firefox
	 * connectivity polling interval. Callers must invoke this before discarding a
	 * ConnectionMonitor — neither the listeners nor the interval are otherwise released,
	 * so they accumulate across reset/recreate cycles.
	 */
	destroy(): void {
		for ( let i: number = 0; i < this.#events.length; i++ ) {
			window.removeEventListener( this.#events[ i ], this.#onConnectionChange );
		}

		if ( this.#utils?.Type?.isDefined( this.#pollIntervalId ) ) {
			clearInterval( this.#pollIntervalId );
			this.#pollIntervalId = undefined;
		}
	}

}